import { InputAdornment, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import {
  CustomButton,
  DefaultLoader,
  InputBasic,
  NumberFormatCustom,
  StepLabel,
} from "../../components";
import { setStep } from "../../globalRedux/actions";
import { dataLayerGTM } from "../../utils/functions/dataLayerGTM";
import { sendFundsCode } from "../fundSelection/redux/action";
import { setLastAmountInvest } from "../fundSelectionStep2/redux/actions";

import { getRoundedValue } from "../../utils/functions/getRoundedValue";
import {
  getRetirementMaxAmount,
  getRetirementMinAmount,
} from "../getStarted/redux/actions";
import "./style.deposit.scss";
import { usePromoBtnState } from "../../utils/hooks";
import {
  getPromo,
  resetSelectedPromo,
  setSelectedPromo,
} from "../promoList/redux/action";
import { getSelectedPromoByCode } from "../../utils/functions/getSelectedPromoByCode";
import { RETIRE } from "../../utils/constants";

const numberFormatter = amount =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "code",
    maximumSignificantDigits: 3,
  })
    .format(amount)
    .replace("USD", "")
    .trim();

function RetiredCashin({
  fundsCode,
  history,
  setLastAmountInvest,
  lastInvestAmount,
  sendFundsCode,
  getRetirementMinAmount,
  getRetirementMinAmountLoading,
  retirementMinAmount,
  selectedPromo,
  getPromo,
  promoList = [],
  promoListLoading,
  setSelectedPromo,
  resetSelectedPromo,
  getRetirementMaxAmount,
  getRetirementMaxAmountLoading,
  retirementMaxAmount,
}) {
  const [state, setState] = useState({
    clientCapturedAmount: lastInvestAmount || 0,
    clientHasError: false,
    clientErrorMessage: "",
  });
  const { clientHasError, clientCapturedAmount, clientErrorMessage } = state;
  const [isErrorPromo, setIsErrorPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");

  const formattedMinAmount = numberFormatter(retirementMinAmount);
  const formattedMaxAmount = numberFormatter(retirementMaxAmount);

  const totalFundsAmount = Array.isArray(fundsCode)
    ? fundsCode.length * clientCapturedAmount
    : 0;

  useEffect(() => {
    getRetirementMinAmount();
    getRetirementMaxAmount();
    getPromo({
      target: RETIRE,
      instrumentId: Array.isArray(fundsCode) ? fundsCode.at(0).code || "" : "",
    });
  }, []);

  // START: Campaign related changes
  useEffect(() => {
    const roundedClientCapturedAmount = getRoundedValue(
      clientCapturedAmount,
      2
    );

    if (roundedClientCapturedAmount) {
      if (roundedClientCapturedAmount < retirementMinAmount) {
        setState({
          ...state,
          clientHasError: true,
          clientErrorMessage: `Minimum RM ${formattedMinAmount}`,
        });
      } else if (roundedClientCapturedAmount > retirementMaxAmount) {
        setState({
          ...state,
          clientHasError: true,
          clientErrorMessage: `Maximum RM ${formattedMaxAmount}`,
        });
      } else {
        setState({
          ...state,
          clientHasError: false,
          clientErrorMessage: "",
        });
      }
    }
  }, [clientCapturedAmount, retirementMinAmount, retirementMaxAmount]);

  const promoBtnState = usePromoBtnState(selectedPromo);

  const getPromoByCode = useCallback(getSelectedPromoByCode(promoList), [
    promoList,
  ]);

  const hasMatchingPromo = val => {
    let matchingPromoCode;
    if (Array.isArray(promoList)) {
      matchingPromoCode = getPromoByCode(val);
    }

    if (matchingPromoCode.length > 0) {
      setSelectedPromo(matchingPromoCode[0]);
    } else {
      setIsErrorPromo(true);
    }
  };

  const goPromoList = () => {
    setLastAmountInvest(clientCapturedAmount);
    if (
      Object.keys(selectedPromo).length > 0 &&
      selectedPromo.selectedReferralCode
    ) {
      history.push("/promo-list", {
        referralCode: selectedPromo.selectedReferralCode,
      });
      return;
    }
    if (
      Object.keys(selectedPromo).length > 0 &&
      !selectedPromo.selectedReferralCode
    ) {
      history.push("/promo-list", { promoId: selectedPromo.campaignId });
      return;
    }
    history.push("/promo-list");
  };

  const handlePromo = () => {
    if (promoBtnState.promo) {
      resetSelectedPromo();
      setState({
        ...state,
        clientHasError: false,
        clientErrorMessage: "Minimum RM 10.00",
      });
      setPromoCode("");
      return;
    }
    if (promoCode === "" || !promoCode) {
      setIsErrorPromo(true);
      return;
    }
    hasMatchingPromo(promoCode);
  };
  // END: Campaign related changes

  const handleChange = ({ target: { value } }) => {
    let slice = { clientCapturedAmount: value };

    const roundedValue = getRoundedValue(value, 2);

    if (roundedValue) {
      if (+roundedValue < +retirementMinAmount) {
        slice = {
          ...slice,
          clientHasError: true,
          clientErrorMessage: `Minimum RM ${formattedMinAmount}`,
        };
      } else if (+roundedValue > +retirementMaxAmount) {
        slice = {
          ...slice,
          clientHasError: true,
          clientErrorMessage: `Maximum RM ${formattedMaxAmount}`,
        };
      } else {
        slice = {
          ...slice,
          clientHasError: false,
          clientErrorMessage: "",
        };
      }
    }

    setState(prev => ({ ...prev, ...slice }));
  };

  const handleSubmit = () => {
    const roundedClientCapturedAmount = getRoundedValue(
      clientCapturedAmount,
      2
    );

    dataLayerGTM(
      "click_tag",
      "Click",
      "Button",
      `Retirement Cash In - Continue`
    );

    dataLayerGTM(
      "click_tag",
      "Input",
      "Amount that user enter",
      `Retirement Cash In - RM${numberFormatter(roundedClientCapturedAmount)}`
    );

    if (+roundedClientCapturedAmount <= 0)
      return setState(prev => ({
        ...prev,
        clientHasError: true,
        clientErrorMessage: "This field is required",
      }));

    if (+roundedClientCapturedAmount < +retirementMinAmount)
      return setState(prev => ({
        ...prev,
        clientHasError: true,
        clientErrorMessage: `Minimum RM ${formattedMinAmount}`,
      }));

    // START: Campaign related changes
    let matchingPromoCode = [];

    // we check if `selectedPromo` not exist if so then get the matching promo code else put `selectedPromo` inside matchingPromoCode
    if (!Object.keys(selectedPromo).length) {
      if (Array.isArray(promoList)) {
        matchingPromoCode = getPromoByCode(promoCode);
      }
    } else {
      matchingPromoCode = [selectedPromo];
    }

    // in case matchingPromoCode is empty array
    // we dont want to have undefined here
    const promo = matchingPromoCode.length ? matchingPromoCode[0] : {};

    setSelectedPromo(promo);

    // save `is promo selected` reference here
    const isPromoSelected = Object.keys(promo).length > 0;

    const isAmountLessThanPromoEligibilityRule =
      isPromoSelected &&
      roundedClientCapturedAmount <
        parseInt(promo.eligibilityRules.minimumSingleDeposit);

    if (isAmountLessThanPromoEligibilityRule) {
      setState(prev => ({
        ...prev,
        clientHasError: true,
        clientErrorMessage: `Minimum campaign cash in RM${
          promo.eligibilityRules.minimumSingleDeposit
        } not met`,
      }));
      return;
    }
    // END: Campaign related changes
    if (+roundedClientCapturedAmount > +retirementMaxAmount)
      return setState(prev => ({
        ...prev,
        clientHasError: true,
        clientErrorMessage: `Maximum RM ${formattedMaxAmount}`,
      }));

    setLastAmountInvest(clientCapturedAmount);

    sendFundsCode([
      {
        ...fundsCode[0],
        clientCapturedAmount: roundedClientCapturedAmount,
      },
    ]);

    history.push("/retirement/summary");
  };

  if (
    getRetirementMinAmountLoading ||
    getRetirementMaxAmountLoading ||
    promoListLoading
  )
    return <DefaultLoader />;

  return (
    <>
      <div className="l-retired-cashin">
        <StepLabel
          title="Cash In"
          containerStyles={{ margin: "0px" }}
          onClick={() => {
            dataLayerGTM(
              "click_tag",
              "Click",
              "Button",
              `Retirement Cash In - back button`
            );
            setLastAmountInvest(0);
            history.back();
          }}
        />
      </div>
      <form
        className="l-retired-cashin__form"
        onSubmit={e => e.preventDefault()}
      >
        <div className="l-retired-cashin__wrapper">
          <p className="l-retired-cashin__title">
            How much are you willing to invest?
          </p>

          <p className="l-retired-cashin__label">{fundsCode[0].name}</p>

          <TextField
            fullWidth
            // style={{ minWidth: "100%", maxHeight: "48px" }}
            variant="outlined"
            placeholder={(Math.round(retirementMinAmount * 100) / 100).toFixed(
              2
            )}
            value={clientCapturedAmount === 0 ? "" : clientCapturedAmount}
            onChange={handleChange}
            error={clientHasError}
            helperText={clientHasError && clientErrorMessage}
            FormHelperTextProps={{
              style: {
                margin: "8px 0 0 0",
                fontFamily: "FSElliotPro",
                fontSize: "14px",
                fontWeight: "normal",
                lineHeight: 1.43,
                color: "#be0712",
              },
            }}
            inputProps={{
              style: {
                fontFamily: "FSElliotPro",
                fontSize: "16px",
                fontWeight: "normal",
                lineHeight: 1.5,
                padding: "12px 16px 12px 0",
                borderColor: "rgba(51, 51, 51, 0.2)",
              },
            }}
            InputProps={{
              inputComponent: NumberFormatCustom,
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    color="textPrimary"
                    sx={{
                      fontWeight: "700",
                    }}
                  >
                    RM
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </div>
        {/* START: Campaign related changes */}
        <div className="border-line" />
        <div className="l-retired-box1">
          <InputBasic
            promoValue={promoBtnState.promo}
            error={isErrorPromo}
            errorMessage="Please enter a valid Promo Code"
            title="Promo Code"
            placeholder="Enter promo code"
            type="text"
            isCurrency={false}
            btnOption={promoBtnState.message}
            textButton={promoBtnState.styleBtn}
            isPromo={true}
            onChange={e => {
              setIsErrorPromo(false);
              setPromoCode(e.target.value);
            }}
            listPromoAction={() => goPromoList()}
            onButton={e => {
              e.preventDefault();
              handlePromo();
            }}
          />
        </div>
        <div className="border-line" />
        <div className="l-retired-box2">
          <div>
            <p>Cash In</p>
            <p>
              RM
              <NumberFormat
                value={totalFundsAmount}
                thousandSeparator
                displayType="text"
                decimalScale={2}
                fixedDecimalScale={true}
              />
            </p>
          </div>

          <div>
            <p>
              Inclusive of Sales Charge
              <br />
              <span style={{ fontSize: "12px", fontWeight: 400 }}>
                ({fundsCode[0].serviceTaxPerc || 0}% SST applicable on Sales
                Charge)
              </span>
            </p>
            <p>
              {fundsCode[0].salesCharge ||
                process.env.REACT_APP_RETIREMENT_SALES_CHARGE.trim()}
              %
            </p>
          </div>
        </div>
        <div className="border-line" />
        {/* END: Campaign related changes */}
        <div className="l-retired-recipe">
          <p className="l-retired-recipe__label">Total Cash In</p>
          <p className="l-retired-recipe__amount">
            RM{" "}
            <NumberFormat
              value={totalFundsAmount}
              thousandSeparator
              displayType="text"
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </p>
        </div>
        <div className="l-retired-cashin__submit">
          <CustomButton
            text="Continue"
            variant="contained"
            color="#fff"
            backgroundColor="#1278CC"
            disabled={clientHasError}
            onClick={handleSubmit}
          />
        </div>
      </form>
    </>
  );
}

const withConnect = connect(
  state => ({
    custFundDetail: state.custFundDetailReducer.custFundDetail,
    fundsCode: state.fundSelectionReducer.fundsCode,
    investMinAmount: state.fundSelectionReducer.minAmount,
    lastInvestAmount: state.investPortfolioReducer.lastInvestAmount,
    retirementMinAmount: state.getStartedReducer.retirementMinAmount,
    retirementMaxAmount: state.getStartedReducer.retirementMaxAmount,
    getRetirementMinAmountLoading:
      state.getStartedReducer.getRetirementMinAmountLoading,
    getRetirementMaxAmountLoading:
      state.getStartedReducer.getRetirementMaxAmountLoading,
    ...state.promoReducer,
  }),
  {
    setStep,
    setLastAmountInvest,
    sendFundsCode,
    getRetirementMinAmount,
    getRetirementMaxAmount,
    getPromo,
    setSelectedPromo,
    resetSelectedPromo,
  }
);

export default compose(
  withRouter,
  withConnect
)(RetiredCashin);
